import { useState, useCallback, useEffect } from 'react';
import { useTelemetry } from '@/hooks/useTelemetry';
import { sendRpc } from '@/lib/tbApi';
import {
  RefreshCw, Clock, Thermometer, Droplets, Wind, Sun,
  ChevronDown, ChevronUp, FlaskConical, Leaf, Zap,
  Fan, Droplet, Lightbulb, RotateCcw, RotateCw, Square
} from 'lucide-react';
import { formatDateTime } from '@/lib/utils';
import { AutomationSection } from './AutomationSection';
import { WeatherCard } from '@/components/greenhouse/WeatherCard';

interface KpsGreenhousePageProps {
  project: string;
  gh: string;
  isOnline: boolean | null;
}

type GhTab = 'gh1' | 'gh2' | 'fert';

const ENV_KEYS_L1 = ['TempAir_L1', 'humidity_L1', 'co2_L1', 'lux_L1'];
const ENV_KEYS_L2 = ['TempAir_L2', 'humidity_L2', 'co2_L2', 'lux_L2'];

function generateSoilKeys(layer: 'L1' | 'L2'): string[] {
  const keys: string[] = [];
  for (let zone = 1; zone <= 4; zone++) {
    for (let point = 1; point <= 2; point++) {
      ['Humidity', 'Temp', 'Con', 'pH', 'Nitro', 'Phos', 'Potas'].forEach(k => {
        keys.push(`${k}_${layer}_${zone}_${point}`);
      });
    }
  }
  return keys;
}

const SOIL_KEYS_L1 = generateSoilKeys('L1');
const SOIL_KEYS_L2 = generateSoilKeys('L2');

function generateControlKeys(layer: 'L1' | 'L2'): string[] {
  const keys: string[] = [];
  keys.push(`fan_${layer}_1_st`, `fan_${layer}_2_st`);
  keys.push(`pump_s${layer}_st`);
  for (let z = 1; z <= 4; z++) keys.push(`valve_f${layer}_${z}_st`);
  for (let z = 1; z <= 4; z++)
    for (let p = 1; p <= 2; p++) keys.push(`valve_d${layer}_${z}_${p}_st`);
  if (layer === 'L2') {
    for (let z = 1; z <= 4; z++) keys.push(`light_${layer}_${z}_st`);
    for (let z = 1; z <= 4; z++) {
      keys.push(`motor_${layer}_${z}_stf`, `motor_${layer}_${z}_str`);
    }
  }
  return keys;
}

const CTRL_KEYS_L1 = generateControlKeys('L1');
const CTRL_KEYS_L2 = generateControlKeys('L2');

function isOn(val: number | string | null | undefined): boolean {
  return val === 1 || val === '1';
}

// ─── Section Header ────────────────────────────────────────────
function SectionHeader({ title, accentColor }: { title: string; accentColor: string }) {
  return (
    <div className="flex items-center gap-2.5 px-4 pt-4 pb-2">
      <div className="w-1 h-5 rounded-full" style={{ background: accentColor }} />
      <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{title}</span>
    </div>
  );
}

// ─── Environment ───────────────────────────────────────────────
function EnvCard({ label, value, unit, icon: Icon, iconColor }: {
  label: string; value: number | string | null; unit: string;
  icon: React.ElementType; iconColor: string;
}) {
  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
        <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      </div>
      <div className="text-xl font-medium text-gray-900 dark:text-gray-100 leading-none">
        {value !== null ? `${value}` : '--'}
        <span className="text-xs font-normal text-gray-400 ml-1">{unit}</span>
      </div>
    </div>
  );
}

function EnvironmentSection({ data, isLoading, layer }: {
  data: Record<string, number | string | null>; isLoading: boolean; layer: 'L1' | 'L2';
}) {
  const items = [
    { key: `TempAir_${layer}`, label: 'อุณหภูมิอากาศ', unit: '°C', icon: Thermometer, iconColor: '#E24B4A' },
    { key: `humidity_${layer}`, label: 'ความชื้น', unit: '%', icon: Droplets, iconColor: '#378ADD' },
    { key: `co2_${layer}`, label: 'CO₂', unit: 'ppm', icon: Wind, iconColor: '#1D9E75' },
    { key: `lux_${layer}`, label: 'แสง', unit: 'lux', icon: Sun, iconColor: '#EF9F27' },
  ];
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden mb-3">
      <SectionHeader title="สภาพแวดล้อมในโรงเรือน" accentColor="#378ADD" />
      <div className="px-3 pb-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {items.map(({ key, label, unit, icon, iconColor }) => (
            <EnvCard key={key} label={label} value={isLoading ? null : (data[key] ?? null)} unit={unit} icon={icon} iconColor={iconColor} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Soil ──────────────────────────────────────────────────────
function SoilValueRow({ label, value, unit, color }: {
  label: string; value: number | string | null; unit: string; color: string;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50 dark:border-gray-700 last:border-0">
      <span className={`text-xs font-medium ${color}`}>{label}</span>
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
        {value !== null ? `${value}${unit ? ' ' + unit : ''}` : '--'}
      </span>
    </div>
  );
}

function SoilPointCard({ title, data, isLoading, layer, zone, point }: {
  title: string; data: Record<string, number | string | null>; isLoading: boolean;
  layer: 'L1' | 'L2'; zone: number; point: number;
}) {
  const prefix = `_${layer}_${zone}_${point}`;
  const items = [
    { key: `Humidity${prefix}`, label: 'ความชื้นดิน', unit: '%', color: 'text-blue-500' },
    { key: `Temp${prefix}`, label: 'อุณหภูมิดิน', unit: '°C', color: 'text-red-400' },
    { key: `Con${prefix}`, label: 'EC', unit: 'µS/cm', color: 'text-purple-500' },
    { key: `pH${prefix}`, label: 'pH', unit: '', color: 'text-yellow-600' },
    { key: `Nitro${prefix}`, label: 'N', unit: 'mg/kg', color: 'text-green-600' },
    { key: `Phos${prefix}`, label: 'P', unit: 'mg/kg', color: 'text-orange-500' },
    { key: `Potas${prefix}`, label: 'K', unit: 'mg/kg', color: 'text-teal-500' },
  ];
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 flex-1 min-w-0">
      <div className="flex items-center gap-1.5 mb-2">
        <Leaf className="w-3 h-3 text-green-500" />
        <span className="text-xs font-medium text-green-700 dark:text-green-400">{title}</span>
      </div>
      {items.map(({ key, label, unit, color }) => (
        <SoilValueRow key={key} label={label} value={isLoading ? null : (data[key] ?? null)} unit={unit} color={color} />
      ))}
    </div>
  );
}

function SoilZoneCard({ zone, data, isLoading, layer }: {
  zone: number; data: Record<string, number | string | null>; isLoading: boolean; layer: 'L1' | 'L2';
}) {
  return (
    <div className="border-t border-gray-50 dark:border-gray-800 first:border-t-0">
      <div className="flex items-center gap-2 px-4 py-2">
        <FlaskConical className="w-3.5 h-3.5 text-amber-600" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Zone {zone}</span>
      </div>
      <div className="px-3 pb-3 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex gap-2">
          <SoilPointCard title="Point 1" data={data} isLoading={isLoading} layer={layer} zone={zone} point={1} />
          <SoilPointCard title="Point 2" data={data} isLoading={isLoading} layer={layer} zone={zone} point={2} />
        </div>
      </div>
    </div>
  );
}

function SoilSection({ data, isLoading, layer }: {
  data: Record<string, number | string | null>; isLoading: boolean; layer: 'L1' | 'L2';
}) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden mb-3">
      <SectionHeader title="ข้อมูลค่าดิน" accentColor="#BA7517" />
      <div className="pb-1">
        {[1, 2, 3, 4].map(zone => (
          <SoilZoneCard key={zone} zone={zone} data={data} isLoading={isLoading} layer={layer} />
        ))}
      </div>
    </div>
  );
}


// ─── Electric ──────────────────────────────────────────────────
const ELECTRIC_KEYS = ['ActEnergy_1', 'Volt_1', 'Current_1', 'PF_1', 'Freq_1'];
const FERT_KEYS = [
  'Fert_Temp', 'Fert_Con', 'Fert_pH',
  'valve_fe_a_st', 'valve_fe_b_st', 'valve_fe_pH1_st', 'valve_fe_pH2_st',
  'valve_fe_sp1_st', 'valve_fe_sp2_st',
  'auto_ec_a_st', 'auto_ec_b_st', 'auto_pH1_st', 'auto_pH2_st',
  'auto_sp1_st', 'auto_sp2_st',
  'fert_plc_st', 'plc_auto_st', 'plc_manual_st', 'plc_emer_st',
  'job_inprogress_st', 'job_finished_st', 'job_stop_st',
];

function ElectricSection({ data, isLoading }: {
  data: Record<string, number | string | null>; isLoading: boolean;
}) {
  const items = [
    { key: 'Volt_1',      label: 'แรงดัน',      unit: 'V',   color: '#F59E0B' },
    { key: 'Current_1',   label: 'กระแส',        unit: 'A',   color: '#EF4444' },
    { key: 'PF_1',        label: 'Power Factor', unit: '',    color: '#8B5CF6' },
    { key: 'Freq_1',      label: 'ความถี่',       unit: 'Hz',  color: '#06B6D4' },
    { key: 'ActEnergy_1', label: 'พลังงานสะสม',  unit: 'kWh', color: '#10B981' },
  ];
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden mb-3">
      <SectionHeader title="ไฟฟ้า" accentColor="#F59E0B" />
      <div className="px-3 pb-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {items.map(({ key, label, unit, color }) => {
            const val = isLoading ? null : (data[key] ?? null);
            return (
              <div key={key} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                  <Zap className="w-3.5 h-3.5" style={{ color }} />
                  <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
                </div>
                <div className="text-xl font-medium text-gray-900 dark:text-gray-100 leading-none">
                  {val !== null ? `${val}` : '--'}
                  {unit && <span className="text-xs font-normal text-gray-400 ml-1">{unit}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Control ───────────────────────────────────────────────────
function ToggleButton({ label, on, loading, onClick, icon: Icon, disabled }: {
  label: string; on: boolean; loading: boolean;
  onClick: () => void; icon: React.ElementType; disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl transition-all min-w-0
        ${on
          ? 'bg-green-50 dark:bg-green-900/30 border-2 border-green-400 dark:border-green-600'
          : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}
        ${(loading || disabled) ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-95 cursor-pointer'}
      `}
    >
      <Icon className={`w-5 h-5 ${on ? 'text-green-600' : 'text-gray-400'} ${loading ? 'animate-pulse' : ''}`} />
      <span className={`text-xs font-medium text-center leading-tight ${on ? 'text-green-700 dark:text-green-400' : 'text-gray-500'}`}>{label}</span>
      <span className={`text-[10px] font-semibold ${on ? 'text-green-500' : 'text-gray-300'}`}>
        {loading ? '...' : on ? 'เปิด' : 'ปิด'}
      </span>
    </button>
  );
}

function MotorCard({ zone, data, optimistic, setOptimistic, project, gh, layer, onRefetch }: {
  zone: number;
  data: Record<string, number | string | null>;
  optimistic: Record<string, number>;
  setOptimistic: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  project: string; gh: string; layer: 'L2'; onRefetch: () => void;
}) {
  const [loadingF, setLoadingF] = useState(false);
  const [loadingR, setLoadingR] = useState(false);
  const [loadingS, setLoadingS] = useState(false);
  const resolve = (key: string) => optimistic[key] ?? data[key];
  const stf = isOn(resolve(`motor_${layer}_${zone}_stf`));
  const str = isOn(resolve(`motor_${layer}_${zone}_str`));
  const isRunning = stf || str;

  const send = useCallback(async (
    method: string, params: number,
    setLoading: (v: boolean) => void,
    optimisticUpdates: Record<string, number>
  ) => {
    setOptimistic(o => ({ ...o, ...optimisticUpdates }));
    setLoading(true);
    try {
      await sendRpc(project, gh, method, params);
    } catch (e) {
      console.error(e);
      setOptimistic(o => {
        const next = { ...o };
        Object.keys(optimisticUpdates).forEach(k => delete next[k]);
        return next;
      });
    } finally { setLoading(false); }
  }, [project, gh, setOptimistic]);

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center gap-2 mb-2">
        <RotateCw className="w-3.5 h-3.5 text-indigo-400" />
        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Motor {zone}</span>
        <span className={`ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full
          ${stf ? 'bg-blue-100 text-blue-600' : str ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-400'}`}>
          {stf ? 'Forward' : str ? 'Reverse' : 'หยุด'}
        </span>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => send(`set_motor_${layer}_${zone}_cmdf`, stf ? 0 : 1, setLoadingF, { [`motor_${layer}_${zone}_stf`]: stf ? 0 : 1 })}
          disabled={loadingF || loadingR || loadingS || str}
          className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium border transition-all
            ${stf ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'}
            ${(loadingF || str) ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}`}
        >
          <RotateCw className="w-3 h-3" />
          {loadingF ? '...' : stf ? 'หยุด F' : 'Forward'}
        </button>
        <button
          onClick={() => send(`set_motor_${layer}_${zone}_cmdr`, str ? 0 : 1, setLoadingR, { [`motor_${layer}_${zone}_str`]: str ? 0 : 1 })}
          disabled={loadingF || loadingR || loadingS || stf}
          className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium border transition-all
            ${str ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'}
            ${(loadingR || stf) ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}`}
        >
          <RotateCcw className="w-3 h-3" />
          {loadingR ? '...' : str ? 'หยุด R' : 'Reverse'}
        </button>
        {isRunning && (
          <button
            onClick={() => {
              const method = stf ? `set_motor_${layer}_${zone}_cmdf` : `set_motor_${layer}_${zone}_cmdr`;
              const updates = stf ? { [`motor_${layer}_${zone}_stf`]: 0 } : { [`motor_${layer}_${zone}_str`]: 0 };
              send(method, 0, setLoadingS, updates);
            }}
            disabled={loadingF || loadingR || loadingS}
            className="px-3 py-2 rounded-lg text-xs border border-red-200 text-red-400 bg-white dark:bg-gray-700 hover:bg-red-50 transition-all"
          >
            <Square className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function ZoneControlCard({ zone, layer, data, optimistic, setOptimistic, project, gh, toggle, sending, onRefetch }: {
  zone: number; layer: 'L1' | 'L2';
  data: Record<string, number | string | null>;
  optimistic: Record<string, number>;
  setOptimistic: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  project: string; gh: string;
  toggle: (method: string, stKey: string, currentOn: boolean) => Promise<void>;
  sending: Record<string, boolean>; onRefetch: () => void;
}) {
  const T = (key: string) => isOn(optimistic[key] !== undefined ? optimistic[key] : data[key]);
  const L = (method: string) => !!sending[method];

  return (
    <div className="border-t border-gray-50 dark:border-gray-800 first:border-t-0">
      <div className="flex items-center gap-2 px-4 py-2">
        <Zap className="w-3.5 h-3.5 text-green-500" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Zone {zone}</span>
      </div>

      <div className="px-3 pb-3 bg-gray-50 dark:bg-gray-800/50 space-y-3">
        <div>
          <p className="text-[10px] font-medium text-gray-400 mb-2 uppercase tracking-wide pt-2">
            วาล์วพ่นหมอก{layer === 'L2' ? ' & ไฟ' : ''}
          </p>
          <div className="grid grid-cols-3 gap-2">
            <ToggleButton
              label="Valve_f"
              on={T(`valve_f${layer}_${zone}_st`)}
              loading={L(`set_valve_f${layer}_${zone}_cmd`)}
              icon={Droplet}
              onClick={() => toggle(`set_valve_f${layer}_${zone}_cmd`, `valve_f${layer}_${zone}_st`, T(`valve_f${layer}_${zone}_st`))}
            />
            {layer === 'L2' && (
              <ToggleButton
                label="ไฟ"
                on={T(`light_${layer}_${zone}_st`)}
                loading={L(`set_light_${layer}_${zone}_cmd`)}
                icon={Lightbulb}
                onClick={() => toggle(`set_light_${layer}_${zone}_cmd`, `light_${layer}_${zone}_st`, T(`light_${layer}_${zone}_st`))}
              />
            )}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-medium text-gray-400 mb-2 uppercase tracking-wide">วาล์วน้ำหยด</p>
          <div className="grid grid-cols-2 gap-2">
            {[1, 2].map(point => (
              <ToggleButton
                key={point}
                label={`Drip P${point}`}
                on={T(`valve_d${layer}_${zone}_${point}_st`)}
                loading={L(`set_valve_d${layer}_${zone}_${point}_cmd`)}
                icon={Droplets}
                onClick={() => toggle(`set_valve_d${layer}_${zone}_${point}_cmd`, `valve_d${layer}_${zone}_${point}_st`, T(`valve_d${layer}_${zone}_${point}_st`))}
              />
            ))}
          </div>
        </div>

        {layer === 'L2' && (
          <div>
            <p className="text-[10px] font-medium text-gray-400 mb-2 uppercase tracking-wide">มอเตอร์</p>
            <MotorCard
              zone={zone} data={data} optimistic={optimistic} setOptimistic={setOptimistic}
              project={project} gh={gh} layer="L2" onRefetch={onRefetch}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function WholeGreenhouseSection({ data, layer, project, gh, optimistic, sending, setSending, setOptimistic }: {
  data: Record<string, number | string | null>; layer: 'L1' | 'L2'; project: string; gh: string;
  optimistic: Record<string, number>; sending: Record<string, boolean>;
  setSending: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setOptimistic: React.Dispatch<React.SetStateAction<Record<string, number>>>;
}) {
  const isOn = (v: unknown) => v === 1 || v === '1' || v === true;
  const resolve = (key: string) => optimistic[key] !== undefined ? optimistic[key] : data[key];
  const T = (key: string) => isOn(resolve(key));

  const sendAll = async (methods: string[], stKeys: string[], turnOn: boolean) => {
    const updates: Record<string, number> = {};
    stKeys.forEach(k => { updates[k] = turnOn ? 1 : 0; });
    setOptimistic(o => ({ ...o, ...updates }));
    methods.forEach(m => setSending(s => ({ ...s, [m]: true })));
    try {
      await Promise.all(methods.map(m => sendRpc(project, gh, m, turnOn ? 1 : 0)));
    } catch (e) {
      console.error(e);
      setOptimistic(o => { const next = { ...o }; stKeys.forEach(k => delete next[k]); return next; });
    } finally {
      methods.forEach(m => setSending(s => ({ ...s, [m]: false })));
    }
  };

  // Fan
  const fanMethods = [`set_fan_${layer}_1_cmd`, `set_fan_${layer}_2_cmd`];
  const fanKeys = [`fan_${layer}_1_st`, `fan_${layer}_2_st`];
  const fanOnCount = fanKeys.filter(k => T(k)).length;
  const allFanOn = fanOnCount === fanKeys.length;
  const fanOnNames = ['Fan1','Fan2'].filter((_, i) => T(fanKeys[i]));

  // Valve_f (Zone 1-4)
  const vfMethods = [1,2,3,4].map(z => `set_valve_f${layer}_${z}_cmd`);
  const vfKeys = [1,2,3,4].map(z => `valve_f${layer}_${z}_st`);
  const vfOnCount = vfKeys.filter(k => T(k)).length;
  const allVfOn = vfOnCount === vfKeys.length;
  const vfOnNames = [1,2,3,4].filter(z => T(`valve_f${layer}_${z}_st`)).map(z => `Z${z}`);

  // Valve_d (Zone 1-4, Point 1-2)
  const vdMethods = [1,2,3,4].flatMap(z => [1,2].map(p => `set_valve_d${layer}_${z}_${p}_cmd`));
  const vdKeys = [1,2,3,4].flatMap(z => [1,2].map(p => `valve_d${layer}_${z}_${p}_st`));
  const vdOnCount = vdKeys.filter(k => T(k)).length;
  const allVdOn = vdOnCount === vdKeys.length;
  const vdOnNames = [1,2,3,4].flatMap(z => [1,2].filter(p => T(`valve_d${layer}_${z}_${p}_st`)).map(p => `Z${z}-P${p}`));

  const isSendingFan = fanMethods.some(m => sending[m]);
  const isSendingVf = vfMethods.some(m => sending[m]);
  const isSendingVd = vdMethods.some(m => sending[m]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden mb-3">
      <SectionHeader title="ควบคุมทั้งโรงเรือน" accentColor="#0E8A6E" />
      <div className="px-3 pb-3 pt-1 space-y-2">

        {/* Fan รวม */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 rounded-xl px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Fan className="w-4 h-4 text-gray-500" />
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">พัดลมรวม</p>
              {fanOnCount > 0 && !allFanOn && (
                <p className="text-[10px] text-amber-500">เปิดอยู่: {fanOnNames.join(', ')} — กดเพื่อเปิดทั้งหมด</p>
              )}
              {allFanOn && <p className="text-[10px] text-green-500">เปิดทั้งหมด — กดเพื่อปิด</p>}
              {fanOnCount === 0 && <p className="text-[10px] text-gray-400">ปิดทั้งหมด</p>}
            </div>
          </div>
          <button
            disabled={isSendingFan}
            onClick={() => sendAll(fanMethods, fanKeys, !allFanOn)}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-all ${
              allFanOn
                ? 'bg-green-50 border-green-400 text-green-600'
                : 'bg-gray-100 border-gray-200 text-gray-500'
            } ${isSendingFan ? 'opacity-50' : ''}`}
          >
            {isSendingFan ? '...' : allFanOn ? 'ปิดทั้งหมด' : 'เปิดทั้งหมด'}
          </button>
        </div>

        {/* พ่นหมอกรวม */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 rounded-xl px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Droplet className="w-4 h-4 text-blue-400" />
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">พ่นหมอกรวม</p>
              {vfOnCount > 0 && !allVfOn && (
                <p className="text-[10px] text-amber-500">เปิดอยู่: {vfOnNames.join(', ')} — กดเพื่อเปิดทั้งหมด</p>
              )}
              {allVfOn && <p className="text-[10px] text-green-500">เปิดทั้งหมด — กดเพื่อปิด</p>}
              {vfOnCount === 0 && <p className="text-[10px] text-gray-400">ปิดทั้งหมด</p>}
            </div>
          </div>
          <button
            disabled={isSendingVf}
            onClick={() => sendAll(vfMethods, vfKeys, !allVfOn)}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-all ${
              allVfOn
                ? 'bg-green-50 border-green-400 text-green-600'
                : 'bg-gray-100 border-gray-200 text-gray-500'
            } ${isSendingVf ? 'opacity-50' : ''}`}
          >
            {isSendingVf ? '...' : allVfOn ? 'ปิดทั้งหมด' : 'เปิดทั้งหมด'}
          </button>
        </div>

        {/* น้ำหยดรวม */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 rounded-xl px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Droplets className="w-4 h-4 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">น้ำหยดรวม</p>
              {vdOnCount > 0 && !allVdOn && (
                <p className="text-[10px] text-amber-500">เปิดอยู่: {vdOnNames.join(', ')} — กดเพื่อเปิดทั้งหมด</p>
              )}
              {allVdOn && <p className="text-[10px] text-green-500">เปิดทั้งหมด — กดเพื่อปิด</p>}
              {vdOnCount === 0 && <p className="text-[10px] text-gray-400">ปิดทั้งหมด</p>}
            </div>
          </div>
          <button
            disabled={isSendingVd}
            onClick={() => sendAll(vdMethods, vdKeys, !allVdOn)}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-all ${
              allVdOn
                ? 'bg-green-50 border-green-400 text-green-600'
                : 'bg-gray-100 border-gray-200 text-gray-500'
            } ${isSendingVd ? 'opacity-50' : ''}`}
          >
            {isSendingVd ? '...' : allVdOn ? 'ปิดทั้งหมด' : 'เปิดทั้งหมด'}
          </button>
        </div>

      </div>
    </div>
  );
}

function ControlSection({ data, isLoading, layer, project, gh, onRefetch, optimistic, setOptimistic, sending, setSending, toggle }: {
  data: Record<string, number | string | null>; isLoading: boolean;
  layer: 'L1' | 'L2'; project: string; gh: string; onRefetch: () => void;
  optimistic: Record<string, number>; setOptimistic: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  sending: Record<string, boolean>; setSending: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  toggle: (method: string, stKey: string, currentOn: boolean) => Promise<void>;
}) {
  const T = (key: string) => isOn(optimistic[key] !== undefined ? optimistic[key] : data[key]);
  const L = (method: string) => !!sending[method];

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden mb-3">
      <SectionHeader title="ควบคุมแยกโซน" accentColor="#1D9E75" />

      <div className="px-3 pb-3 pt-1">
        <p className="text-[10px] font-medium text-gray-400 mb-2 uppercase tracking-wide">พัดลม & ปั๊ม</p>
        <div className="grid grid-cols-3 gap-2">
          <ToggleButton label="Fan 1" on={T(`fan_${layer}_1_st`)} loading={L(`set_fan_${layer}_1_cmd`)} icon={Fan}
            onClick={() => toggle(`set_fan_${layer}_1_cmd`, `fan_${layer}_1_st`, T(`fan_${layer}_1_st`))} />
          <ToggleButton label="Fan 2" on={T(`fan_${layer}_2_st`)} loading={L(`set_fan_${layer}_2_cmd`)} icon={Fan}
            onClick={() => toggle(`set_fan_${layer}_2_cmd`, `fan_${layer}_2_st`, T(`fan_${layer}_2_st`))} />
          <ToggleButton label="Pump" on={T(`pump_s${layer}_st`)} loading={L(`set_pump_s${layer}_cmd`)} icon={Droplet}
            onClick={() => toggle(`set_pump_s${layer}_cmd`, `pump_s${layer}_st`, T(`pump_s${layer}_st`))} />
        </div>
      </div>

      <div className="pb-1">
        {[1, 2, 3, 4].map(zone => (
          <ZoneControlCard
            key={zone} zone={zone} layer={layer} data={data}
            optimistic={optimistic} setOptimistic={setOptimistic}
            project={project} gh={gh} toggle={toggle} sending={sending} onRefetch={onRefetch}
          />
        ))}
      </div>
    </div>
  );
}


// ─── Fertilizer ────────────────────────────────────────────────
function FertSensorCard({ label, value, target, unit, color }: {
  label: string; value: number | string | null; target?: number | null; unit: string; color: string;
}) {
  const numVal = value !== null && value !== undefined ? parseFloat(String(value)) : null;
  const diff = numVal !== null && target != null ? numVal - target : null;
  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center gap-1">
      <span className="text-xs text-gray-400 dark:text-gray-500">{label}</span>
      <span className={`text-3xl font-medium ${color}`}>
        {numVal !== null ? `${value}` : '--'}
      </span>
      <span className="text-xs text-gray-400">{unit}</span>
      {target != null && (
        <span className="text-[10px] text-gray-400">
          Target: <span className="font-medium text-gray-600 dark:text-gray-300">{target}</span>
          {diff !== null && (
            <span className={`ml-1 font-semibold ${Math.abs(diff) < 50 ? 'text-green-500' : diff > 0 ? 'text-blue-400' : 'text-amber-500'}`}>
              ({diff > 0 ? '+' : ''}{diff.toFixed(1)})
            </span>
          )}
        </span>
      )}
    </div>
  );
}

function FertValveBtn({ label, on, loading, onClick }: {
  label: string; on: boolean; loading: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`rounded-xl p-3 border-2 text-center transition-all active:scale-95 ${
        on
          ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-400 text-emerald-700 dark:text-emerald-300'
          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400'
      } ${loading ? 'opacity-60' : ''}`}
    >
      <div className="text-sm font-medium leading-tight">{label}</div>
      <div className={`text-[10px] mt-0.5 ${on ? 'text-emerald-500' : 'text-gray-400'}`}>
        {loading ? '...' : on ? 'เปิด' : 'ปิด'}
      </div>
    </button>
  );
}

function FertAutoToggle({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`relative inline-flex items-center w-16 h-8 rounded-full transition-colors duration-200 focus:outline-none ${
        active ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span className={`inline-block w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
        active ? 'translate-x-9' : 'translate-x-1'
      }`} />
    </button>
  );
}

function FertilizerSection({ data, isLoading, project, gh, optimistic, setOptimistic, sending, setSending, toggle }: {
  data: Record<string, number | string | null>;
  isLoading: boolean;
  project: string;
  gh: string;
  optimistic: Record<string, number>;
  setOptimistic: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  sending: Record<string, boolean>;
  setSending: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  toggle: (method: string, stKey: string, currentOn: boolean) => Promise<void>;
}) {
  const [ecTarget, setEcTarget] = useState<string>('');
  const [phLow, setPhLow] = useState<string>('');
  const [phHigh, setPhHigh] = useState<string>('');
  const [autoFert, setAutoFert] = useState<boolean>(false);
  const [setpointSaving, setSetpointSaving] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [savedValues, setSavedValues] = useState<{ ec: number; phLow: number; phHigh: number; at: string } | null>(null);
  const [setpointLoading, setSetpointLoading] = useState<boolean>(true);
  const [validationError, setValidationError] = useState<string>('');

  // โหลด setpoint ครั้งแรก
  useEffect(() => {
    setSetpointLoading(true);
    fetch(`/api/fert-setpoints/${project}/gh1`)
      .then(r => r.json())
      .then(res => {
        if (res.success && res.data) {
          const d = res.data;
          setEcTarget(String(d.ec_target ?? ''));
          setPhLow(String(d.ph_low ?? '5.5'));
          setPhHigh(String(d.ph_high ?? '6.5'));
          setAutoFert(d.is_active === 1);
          if (d.updated_at) {
            setSavedValues({ ec: d.ec_target, phLow: d.ph_low, phHigh: d.ph_high, at: d.updated_at });
          }
        }
      })
      .catch(console.error)
      .finally(() => setSetpointLoading(false));
  }, [project]);

  const saveSetpoint = async () => {
    // Validation
    const ecVal = parseFloat(ecTarget);
    const phLowVal = parseFloat(phLow);
    const phHighVal = parseFloat(phHigh);
    if (!ecTarget || isNaN(ecVal) || ecVal <= 0) {
      setValidationError('กรุณากรอก EC Target ให้มากกว่า 0');
      return;
    }
    if (isNaN(phLowVal) || isNaN(phHighVal) || phLowVal >= phHighVal) {
      setValidationError('pH ต่ำสุดต้องน้อยกว่า pH สูงสุด');
      return;
    }
    setValidationError('');
    setSetpointSaving(true);
    setSaveStatus('idle');
    try {
      const res = await fetch(`/api/fert-setpoints/${project}/gh1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ec_target: ecVal,
          ph_low: phLowVal,
          ph_high: phHighVal,
          is_active: autoFert ? 1 : 0,
        }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setSaveStatus('success');
        setSavedValues({ ec: ecVal, phLow: phLowVal, phHigh: phHighVal, at: new Date().toISOString() });
        setTimeout(() => setSaveStatus('idle'), 2500);
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (e) {
      console.error(e);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSetpointSaving(false);
    }
  };

  const val = (key: string) => {
    if (optimistic[key] !== undefined) return optimistic[key];
    return data[key] ?? null;
  };
  const T = (key: string) => isOn(val(key));
  const L = (key: string) => !!sending[key];

  const jobInProgress = T('job_inprogress_st');
  const jobFinished = T('job_finished_st');
  const jobStopped = T('job_stop_st');

  const plcOn = T('fert_plc_st');
  const autoMode = T('plc_auto_st');
  const emerMode = T('plc_emer_st');

  let jobBadgeText = 'ว่าง';
  let jobBadgeColor = 'bg-gray-100 dark:bg-gray-800 text-gray-400';
  if (jobInProgress) { jobBadgeText = 'กำลังทำงาน'; jobBadgeColor = 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'; }
  else if (jobFinished) { jobBadgeText = 'เสร็จแล้ว'; jobBadgeColor = 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'; }
  else if (jobStopped) { jobBadgeText = 'หยุดแล้ว'; jobBadgeColor = 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'; }

  const ecNum = parseFloat(ecTarget) || null;
  const phLowNum = parseFloat(phLow) || null;
  const phHighNum = parseFloat(phHigh) || null;

  const formatSavedAt = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden mb-3">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-1 h-5 rounded-full" style={{ background: '#10B981' }} />
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100">เครื่องผสมปุ๋ย</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${jobBadgeColor}`}>{jobBadgeText}</span>
          {emerMode && <span className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300">ฉุกเฉิน</span>}
        </div>
      </div>

      {/* ค่าน้ำปุ๋ย */}
      <div className="px-3 pb-3">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">ค่าน้ำปุ๋ย</p>
        <div className="grid grid-cols-3 gap-2">
          <FertSensorCard label="อุณหภูมิ" value={isLoading ? null : (data['Fert_Temp'] ?? null)} unit="°C" color="text-red-400" />
          <FertSensorCard label="EC" value={isLoading ? null : (data['Fert_Con'] ?? null)} unit="µS/cm" color="text-purple-500" target={savedValues ? savedValues.ec : null} />
          <FertSensorCard label="pH" value={isLoading ? null : (data['Fert_pH'] ?? null)} unit="" color="text-blue-500" target={savedValues ? ((savedValues.phLow + savedValues.phHigh) / 2) : null} />
        </div>
      </div>

      {/* ตั้งค่า EC/pH Target */}
      <div className="px-3 pb-3">
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-3 border border-emerald-100 dark:border-emerald-800">

          {/* Header: ตั้งค่าอัตโนมัติ + Toggle ใหญ่ + Badge FertEngine */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex flex-col gap-0.5">
              <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wide">ตั้งค่าอัตโนมัติ</p>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full w-fit ${
                autoFert ? 'bg-emerald-200 text-emerald-700' : 'bg-gray-200 text-gray-500'
              }`}>
                FertEngine: {autoFert ? 'เปิด' : 'ปิด'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${autoFert ? 'text-emerald-600' : 'text-gray-400'}`}>
                {autoFert ? 'เปิด' : 'ปิด'}
              </span>
              <FertAutoToggle active={autoFert} onClick={() => setAutoFert(v => !v)} />
            </div>
          </div>

          {/* Inputs */}
          <div className="grid grid-cols-3 gap-2 mb-2">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-medium text-purple-600">EC Target (µS/cm)</label>
              <input
                type="number"
                value={ecTarget}
                onChange={e => { setEcTarget(e.target.value); setValidationError(''); }}
                placeholder="เช่น 1500"
                disabled={setpointLoading}
                className={`w-full px-2 py-2 rounded-xl border text-sm text-center font-medium focus:outline-none transition-colors ${
                  validationError && (!ecTarget || parseFloat(ecTarget) <= 0)
                    ? 'border-red-300 bg-red-50'
                    : 'border-purple-200 focus:border-purple-400'
                } disabled:opacity-50`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-medium text-blue-600">pH ต่ำสุด</label>
              <input
                type="number"
                step="0.1"
                value={phLow}
                onChange={e => { setPhLow(e.target.value); setValidationError(''); }}
                placeholder="เช่น 5.5"
                disabled={setpointLoading}
                className={`w-full px-2 py-2 rounded-xl border text-sm text-center font-medium focus:outline-none transition-colors ${
                  validationError && parseFloat(phLow) >= parseFloat(phHigh)
                    ? 'border-red-300 bg-red-50'
                    : 'border-blue-200 focus:border-blue-400'
                } disabled:opacity-50`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-medium text-blue-600">pH สูงสุด</label>
              <input
                type="number"
                step="0.1"
                value={phHigh}
                onChange={e => { setPhHigh(e.target.value); setValidationError(''); }}
                placeholder="เช่น 6.5"
                disabled={setpointLoading}
                className={`w-full px-2 py-2 rounded-xl border text-sm text-center font-medium focus:outline-none transition-colors ${
                  validationError && parseFloat(phLow) >= parseFloat(phHigh)
                    ? 'border-red-300 bg-red-50'
                    : 'border-blue-200 focus:border-blue-400'
                } disabled:opacity-50`}
              />
            </div>
          </div>

          {/* Validation Error */}
          {validationError && (
            <p className="text-xs text-red-500 mb-2 text-center font-medium">{validationError}</p>
          )}

          {/* Save Button */}
          <button
            onClick={saveSetpoint}
            disabled={setpointSaving || setpointLoading}
            className={`w-full py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-50 ${
              saveStatus === 'success'
                ? 'bg-green-500 text-white'
                : saveStatus === 'error'
                ? 'bg-red-500 text-white'
                : 'bg-emerald-500 hover:bg-emerald-600 text-white'
            }`}
          >
            {setpointSaving ? 'กำลังบันทึก...' : saveStatus === 'success' ? 'บันทึกแล้ว ✓' : saveStatus === 'error' ? 'บันทึกไม่สำเร็จ ลองใหม่' : 'บันทึกค่าเป้าหมาย'}
          </button>

          {/* ค่าล่าสุดที่บันทึก */}
          {savedValues && saveStatus === 'idle' && (
            <p className="text-[10px] text-emerald-600 dark:text-emerald-400 text-center mt-2">
              บันทึกล่าสุด {formatSavedAt(savedValues.at)} — EC {savedValues.ec} / pH {savedValues.phLow}–{savedValues.phHigh}
            </p>
          )}
        </div>
      </div>

      {/* วาล์วปุ๋ย */}
      <div className="px-3 pb-3">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">วาล์วปุ๋ย</p>
        <div className="grid grid-cols-3 gap-2">
          <FertValveBtn label="ปุ๋ย A" on={T('valve_fe_a_st')} loading={L('set_valve_fe_a_cmd')}
            onClick={() => toggle('set_valve_fe_a_cmd', 'valve_fe_a_st', T('valve_fe_a_st'))} />
          <FertValveBtn label="ปุ๋ย B" on={T('valve_fe_b_st')} loading={L('set_valve_fe_b_cmd')}
            onClick={() => toggle('set_valve_fe_b_cmd', 'valve_fe_b_st', T('valve_fe_b_st'))} />
          <FertValveBtn label="เพิ่ม pH" on={T('valve_fe_pH1_st')} loading={L('set_valve_fe_pH1_cmd')}
            onClick={() => toggle('set_valve_fe_pH1_cmd', 'valve_fe_pH1_st', T('valve_fe_pH1_st'))} />
          <FertValveBtn label="ลด pH" on={T('valve_fe_pH2_st')} loading={L('set_valve_fe_pH2_cmd')}
            onClick={() => toggle('set_valve_fe_pH2_cmd', 'valve_fe_pH2_st', T('valve_fe_pH2_st'))} />
          <FertValveBtn label="สปริงเกอร์ 1" on={T('valve_fe_sp1_st')} loading={L('set_valve_fe_sp1_cmd')}
            onClick={() => toggle('set_valve_fe_sp1_cmd', 'valve_fe_sp1_st', T('valve_fe_sp1_st'))} />
          <FertValveBtn label="สปริงเกอร์ 2" on={T('valve_fe_sp2_st')} loading={L('set_valve_fe_sp2_cmd')}
            onClick={() => toggle('set_valve_fe_sp2_cmd', 'valve_fe_sp2_st', T('valve_fe_sp2_st'))} />
        </div>
      </div>

      {/* โหมดอัตโนมัติ */}
      <div className="px-3 pb-3">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">โหมดอัตโนมัติ</p>
        <div className="grid grid-cols-3 gap-2">
          <FertValveBtn label="Auto EC A" on={T('auto_ec_a_st')} loading={L('set_auto_ec_a_cmd')}
            onClick={() => toggle('set_auto_ec_a_cmd', 'auto_ec_a_st', T('auto_ec_a_st'))} />
          <FertValveBtn label="Auto EC B" on={T('auto_ec_b_st')} loading={L('set_auto_ec_b_cmd')}
            onClick={() => toggle('set_auto_ec_b_cmd', 'auto_ec_b_st', T('auto_ec_b_st'))} />
          <FertValveBtn label="Auto pH+" on={T('auto_pH1_st')} loading={L('set_auto_pH1_cmd')}
            onClick={() => toggle('set_auto_pH1_cmd', 'auto_pH1_st', T('auto_pH1_st'))} />
          <FertValveBtn label="Auto pH-" on={T('auto_pH2_st')} loading={L('set_auto_pH2_cmd')}
            onClick={() => toggle('set_auto_pH2_cmd', 'auto_pH2_st', T('auto_pH2_st'))} />
          <FertValveBtn label="Auto Sp1" on={T('auto_sp1_st')} loading={L('set_auto_sp1_cmd')}
            onClick={() => toggle('set_auto_sp1_cmd', 'auto_sp1_st', T('auto_sp1_st'))} />
          <FertValveBtn label="Auto Sp2" on={T('auto_sp2_st')} loading={L('set_auto_sp2_cmd')}
            onClick={() => toggle('set_auto_sp2_cmd', 'auto_sp2_st', T('auto_sp2_st'))} />
        </div>
      </div>

      {/* สถานะ PLC */}
      <div className="px-3 pb-3">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">สถานะ PLC</p>
        <div className="flex flex-wrap gap-2">
          <span className={`text-xs font-medium px-3 py-1 rounded-full ${plcOn ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-400 dark:bg-gray-800'}`}>
            PLC: {plcOn ? 'เปิด' : 'ปิด'}
          </span>
          <span className={`text-xs font-medium px-3 py-1 rounded-full ${autoMode ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-400 dark:bg-gray-800'}`}>
            {autoMode ? 'Auto' : 'Manual'}
          </span>
          {emerMode && (
            <span className="text-xs font-medium px-3 py-1 rounded-full bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300">ฉุกเฉิน</span>
          )}
        </div>
      </div>

      {/* ควบคุม Job */}
      <div className="px-3 pb-4">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">ควบคุมงาน</p>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => toggle('set_job_start_cmd', 'job_inprogress_st', false)}
            disabled={L('set_job_start_cmd') || jobInProgress}
            className={`py-4 rounded-2xl font-medium text-sm transition-all active:scale-95 ${
              jobInProgress
                ? 'bg-emerald-500 text-white opacity-80'
                : 'bg-emerald-500 hover:bg-emerald-600 text-white'
            } disabled:opacity-50`}
          >
            {L('set_job_start_cmd') ? '...' : jobInProgress ? 'กำลังทำงาน' : 'เริ่มงาน'}
          </button>
          <button
            onClick={() => toggle('set_job_stop_cmd', 'job_stop_st', false)}
            disabled={L('set_job_stop_cmd') || !jobInProgress}
            className="py-4 rounded-2xl font-medium text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-red-50 hover:text-red-600 transition-all active:scale-95 disabled:opacity-40"
          >
            {L('set_job_stop_cmd') ? '...' : 'หยุดงาน'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────
export function KpsGreenhousePage({ project, gh, isOnline }: KpsGreenhousePageProps) {
  const [activeTab, setActiveTab] = useState<GhTab>('gh1');

  const layer = activeTab === 'gh1' ? 'L1' : 'L2';
  const envKeys = activeTab === 'gh1' ? ENV_KEYS_L1 : ENV_KEYS_L2;
  const soilKeys = activeTab === 'gh1' ? SOIL_KEYS_L1 : SOIL_KEYS_L2;
  const ctrlKeys = activeTab === 'gh1' ? CTRL_KEYS_L1 : CTRL_KEYS_L2;

  const isReady = true;
  const [sending, setSending] = useState<Record<string, boolean>>({});
  const [optimistic, setOptimistic] = useState<Record<string, number>>({});
  const toggle = useCallback(async (method: string, stKey: string, currentOn: boolean) => {
    setOptimistic(o => ({ ...o, [stKey]: currentOn ? 0 : 1 }));
    setSending(s => ({ ...s, [method]: true }));
    try {
      await sendRpc(project, gh, method, currentOn ? 0 : 1);
    } catch (e) {
      console.error(e);
      setOptimistic(o => { const next = { ...o }; delete next[stKey]; return next; });
    } finally {
      setSending(s => ({ ...s, [method]: false }));
    }
  }, [project, gh]);

  const envData = useTelemetry({ project, gh, keys: envKeys, enabled: isReady, pollInterval: 60000 });
  const soilData = useTelemetry({ project, gh, keys: soilKeys, enabled: isReady, pollInterval: 60000 });
  const ctrlData = useTelemetry({ project, gh, keys: ctrlKeys, enabled: isReady, pollInterval: 5000 });
  const electricData = useTelemetry({ project, gh, keys: ELECTRIC_KEYS, enabled: isReady, pollInterval: 60000 });
  const fertData = useTelemetry({ project, gh, keys: FERT_KEYS, enabled: isReady, pollInterval: 5000 });

  const lastUpdated = envData.lastUpdated;

  const handleRefetchAll = () => {
    envData.refetch();
    soilData.refetch();
    ctrlData.refetch();
    electricData.refetch();
    fertData.refetch();
  };

  return (
    <div className="space-y-0">
      {/* Tab */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {([['gh1', 'โรงเรือนทดลอง 1'], ['gh2', 'โรงเรือนทดลอง 2'], ['fert', 'เครื่องผสมปุ๋ย']] as [GhTab, string][]).map(([tab, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
              activeTab === tab
                ? tab === 'fert' ? 'bg-emerald-500 text-white shadow-sm' : 'bg-green-600 text-white shadow-sm'
                : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-green-300 hover:text-green-600'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Timestamp + Refresh */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Clock className="w-3.5 h-3.5" />
          <span>{lastUpdated ? formatDateTime(new Date(lastUpdated)) : '--'}</span>
        </div>
        <button
          onClick={handleRefetchAll}
          disabled={envData.isLoading || soilData.isLoading || ctrlData.isLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${(envData.isLoading || soilData.isLoading || ctrlData.isLoading) ? 'animate-spin' : ''}`} />
          รีเฟรช
        </button>
      </div>

      {activeTab === 'fert' ? (
        <FertilizerSection
          data={fertData.data} isLoading={fertData.isLoading}
          project={project} gh={gh}
          optimistic={optimistic} setOptimistic={setOptimistic}
          sending={sending} setSending={setSending} toggle={toggle}
        />
      ) : (
        <>
          {/* Weather */}
          <div className="mb-3">
            <WeatherCard project={project} gh={activeTab} />
          </div>
          <EnvironmentSection data={envData.data} isLoading={envData.isLoading} layer={layer} />
          <SoilSection data={soilData.data} isLoading={soilData.isLoading} layer={layer} />
          <ElectricSection data={electricData.data} isLoading={electricData.isLoading} />
          <WholeGreenhouseSection data={ctrlData.data} layer={layer} project={project} gh={gh} optimistic={optimistic} sending={sending} setSending={setSending} setOptimistic={setOptimistic} />
          <AutomationSection layer={layer} project={project} gh={gh} />
          <ControlSection
            data={ctrlData.data} isLoading={ctrlData.isLoading}
            layer={layer} project={project} gh={gh} onRefetch={ctrlData.refetch}
            optimistic={optimistic} setOptimistic={setOptimistic}
            sending={sending} setSending={setSending} toggle={toggle}
          />
        </>
      )}
    </div>
  );
}
